import { Optional } from '@rotomeca/utils';
import { TAG_TOGGLE_BUTTON } from '../../../core/utils/constants/tags';
import { Attr, Autobind, Define } from '../../../core/decorators';
import { Listener } from 'core/decorators/listener';
import { HTMLBnumButton } from '../button/bnum-button';
import {
	ATTR_DISABLED,
	ATTR_LOADING,
} from '../button/bnum-button.internal/constants';
import {
	ATTR_PRESSED,
	STATE_PRESSED,
	PressedChangedEvent,
	OnPressedChangeInitializer,
	BnumToggleButtonCreateOptions,
} from './internal';

/**
 * Bouton Bnum à bascule (pressed / unpressed), utilisé pour représenter un état
 * binaire actionnable au clic (ex : favori, filtre actif, option activée).
 *
 * Hérite de {@link HTMLBnumButton} : variation, icône, état de chargement,
 * arrondi et désactivation se gèrent exactement comme sur un bouton standard.
 * L'aspect visuel de l'état "pressed" (icône, classe, couleur, etc.) est laissé
 * aux composants consommateurs, via l'état CSS `pressed` ou l'événement
 * `onpressedchange`.
 *
 * @category Buttons
 *
 * @structure Cas standard
 * <bnum-toggle-button>Suivre</bnum-toggle-button>
 *
 * @structure Bouton initialement enfoncé
 * <bnum-toggle-button pressed>Suivre</bnum-toggle-button>
 *
 * @structure Bouton à bascule avec variation
 * <bnum-toggle-button data-variation="secondary">Suivre</bnum-toggle-button>
 *
 * @structure Bouton à bascule avec icône
 * <bnum-toggle-button data-icon="star">Favori</bnum-toggle-button>
 *
 * @state pressed - Actif lorsque le bouton est dans l'état "enfoncé"
 *
 * @attr {boolean | undefined} (optional) pressed - État "enfoncé" du bouton
 *
 * @event {ElementChangedEvent<boolean, boolean, HTMLBnumToggleButton>} custom:element-changed.pressed - Déclenché lors du changement de l'état "pressed".
 */
@Define({
	tag: TAG_TOGGLE_BUTTON,
})
export class HTMLBnumToggleButton extends HTMLBnumButton {
	//#region Private fields
	#_lastPressed = false;
	//#endregion Private fields

	//#region Getter/setter
	/**
	 * État "enfoncé" du bouton.
	 */
	@Attr(ATTR_PRESSED)
	accessor pressed!: boolean;

	/**
	 * Événement déclenché lors du changement de l'état "pressed".
	 */
	@Listener(OnPressedChangeInitializer, { lazy: false })
	accessor onpressedchange!: PressedChangedEvent;
	//#endregion Getter/setter

	//#region Lifecycle
	constructor() {
		super();
	}

	protected _p_buildDOM(): void {
		super._p_buildDOM();

		this.addEventListener('click', this._onClick);

		this.#_lastPressed = this.pressed;
		this.#_syncPressedState();
	}

	protected _p_update(): void {
		super._p_update();

		this.#_syncPressedState();

		const newPressed = this.pressed;
		if (newPressed !== this.#_lastPressed) {
			const oldPressed = this.#_lastPressed;
			this.#_lastPressed = newPressed;
			this.onpressedchange.call(newPressed, oldPressed);
		}
	}
	//#endregion Lifecycle

	//#region Private methods
	/**
	 * Synchronise l'état CSS (`:state(pressed)`) et l'accessibilité (`aria-pressed`)
	 * avec l'attribut `pressed`.
	 */
	#_syncPressedState(): void {
		if (this.pressed) this._p_addState(STATE_PRESSED);
		else this._p_removeState(STATE_PRESSED);

		this._p_internal.ariaPressed = String(this.pressed);
	}

	/**
	 * Bascule l'état "pressed" au clic, sauf si le bouton est désactivé ou en chargement.
	 */
	@Autobind
	private _onClick(): void {
		if (this.#_isInteractionBlocked()) return;

		this.togglePressed();
	}

	/**
	 * Indique si le bouton ne doit pas réagir au clic (désactivé ou en chargement).
	 */
	#_isInteractionBlocked(): boolean {
		return (
			this.#_toBool(this.getAttribute(ATTR_DISABLED)) ||
			this.#_toBool(this.getAttribute(ATTR_LOADING))
		);
	}

	/**
	 * Normalise une valeur d'attribut en booléen (présent et différent de `"false"`).
	 */
	#_toBool(value: string | null): boolean {
		return value !== null && value !== 'false';
	}
	//#endregion Private methods

	//#region Public methods
	/**
	 * Met le bouton dans l'état "enfoncé".
	 * @returns L'instance du bouton
	 */
	press(): this {
		this.pressed = true;
		return this;
	}

	/**
	 * Retire l'état "enfoncé" du bouton.
	 * @returns L'instance du bouton
	 */
	unpress(): this {
		this.pressed = false;
		return this;
	}

	/**
	 * Bascule l'état "enfoncé" du bouton.
	 * @returns L'instance du bouton
	 */
	togglePressed(): this {
		this.pressed = !this.pressed;
		return this;
	}
	//#endregion Public methods

	//#region Static methods
	/**
	 * Retourne la liste des attributs observés par le composant.
	 */
	static _p_observedAttributes(): string[] {
		return [...super._p_observedAttributes(), ATTR_PRESSED];
	}

	/**
	 * Crée un bouton à bascule Bnum avec les options spécifiées.
	 * @static
	 * @param options Options de configuration du bouton (dont l'état initial `pressed`)
	 * @returns Instance du bouton créé
	 */
	static Create(
		options?: Optional<BnumToggleButtonCreateOptions>,
	): HTMLBnumToggleButton {
		const node = super.Create(options) as HTMLBnumToggleButton;

		if (options?.pressed) node.pressed = true;

		return node;
	}
	//#endregion Static methods
}
